import { defineStore } from 'pinia'
import { login as apiLogin } from '../api/auth'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem('token') || null,
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    loading: false,
    tokenExpireTime: localStorage.getItem('tokenExpireTime') || null,
    permissionsCache: new Map() // 添加权限缓存
  }),
  
  actions: {
    async login(username, password) {
      this.loading = true
      try {
        const response = await apiLogin(username, password)
        
        if (response.token && response.user) {
          this.token = response.token
          this.user = response.user
          
          // 设置为7天后过期（与服务器端JWT过期时间一致）
          const expireTime = new Date().getTime() + (7 * 24 * 60 * 60 * 1000) // 7天
          this.tokenExpireTime = expireTime
          
          // 保存到 localStorage
          localStorage.setItem('token', response.token)
          localStorage.setItem('user', JSON.stringify(response.user))
          localStorage.setItem('tokenExpireTime', expireTime.toString())
          
          console.log('设置新的过期时间:', {
            loginTime: new Date().toLocaleString(),
            expireTime: new Date(expireTime).toLocaleString(),
            remainingDays: 7
          })
          
          return response
        } else {
          throw new Error('用户名或密码错误')
        }
      } catch (error) {
        console.error('登录错误:', error)
        this.clearAuth()
        throw error
      } finally {
        this.loading = false
      }
    },
    
    logout() {
      this.clearAuth()
    },

    // 清除认证信息
    clearAuth() {
      this.token = null
      this.user = null
      this.tokenExpireTime = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('tokenExpireTime')
      localStorage.removeItem('userCredentials')
    },

    // 检查 token 是否过期
    checkTokenExpiration() {
      if (!this.token || !this.tokenExpireTime) {
        console.log('Token 或过期时间不存在')
        return false
      }

      const now = new Date().getTime()
      const expireTime = parseInt(this.tokenExpireTime)
      const remainingTime = expireTime - now
      
      console.log('Token 检查:', {
        now: new Date(now).toLocaleString(),
        expireTime: new Date(expireTime).toLocaleString(),
        remainingSeconds: Math.round(remainingTime / 1000),
        remainingMinutes: Math.round(remainingTime / (1000 * 60))
      })

      // 如果剩余时间小于等于0，则表示已过期
      if (remainingTime <= 0) {
        console.log('Token 已过期')
        this.clearAuth()
        return false
      }
      return true
    },

    // 添加权限缓存方法
    cachePermission(page, result) {
      this.permissionsCache.set(page, {
        result,
        timestamp: Date.now()
      })
    },
    
    // 优化权限检查
    hasPermission(page, action) {
      console.time(`权限检查 ${page}.${action}`)
      
      if (this.user?.role === 'admin') {
        console.log('✅ 管理员权限验证通过')
        return true
      }
      
      // 检查缓存
      const cached = this.permissionsCache.get(`${page}.${action}`)
      if (cached && Date.now() - cached.timestamp < 5000) {
        console.log(`✅ 使用缓存的权限结果: ${page}.${action}`)
        console.timeEnd(`权限检查 ${page}.${action}`)
        return cached.result
      }
      
      const result = this.user?.page_permissions?.[page]?.[action] === true
      
      // 缓存结果
      this.permissionsCache.set(`${page}.${action}`, {
        result,
        timestamp: Date.now()
      })
      
      console.timeEnd(`权限检查 ${page}.${action}`)
      return result
    }
  },
  
  getters: {
    isAuthenticated: (state) => {
      if (!state.token) {
        console.log('Token 不存在')
        return false
      }
      
      const now = new Date().getTime()
      const expireTime = parseInt(state.tokenExpireTime)
      if (state.tokenExpireTime && now >= expireTime) {
        console.log('Token 已过期 (getter)')
        state.clearAuth()
        return false
      }
      return true
    },
    userRole: (state) => state.user?.role,
    isAdmin: (state) => state.user?.role === 'admin'
  }
})